import React, { useMemo } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ExternalLink, Loader2, MessageSquare } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ChatWindow from '@/components/whatsapp-inbox/ChatWindow';
import type { Conversation } from '@/hooks/useConversations';

// CONNECTION_ATENDIMENTO resolvido em runtime via RPC `whatsapp_get_active_connection`
// com role='atendimento' (substitui o UUID hardcoded antigo f3d7eae4...).

// FASE 3 (26/05): delega para helper único, mesma regra da PG e edge runtime.
import { canonicalizePhoneBR } from '@/utils/phone-canonical';
const canonicalize = canonicalizePhoneBR;

interface Props {
  telefone: string;
  conversationId?: string | null;
  open: boolean;
  onClose: () => void;
  /** (17/09) Faixa de contexto do topo, quando a gaveta é aberta de FORA da
   * Qualidade — o card do Kanban manda aqui de qual pedido, fila e valor é a
   * conversa. Sem ela quem abre pelo card fala com o cliente sem lembrar sobre
   * o que era. */
  contexto?: { titulo: string; detalhe: string } | null;
}

export const MiniConversaSheet: React.FC<Props> = ({ telefone, conversationId, open, onClose, contexto }) => {
  /* ⚠️ O Inbox só entende `?conversation=` (WhatsappInbox.tsx:179). Este botão
     mandava `?conversa=` — a URL abria o Inbox no vazio ("Selecione uma conversa")
     e a conversa do cliente nunca era aberta. E abria na MESMA aba, fechando a
     gaveta: quem estava na fila perdia o lugar. Agora é igual ao ✆/balão do
     Painel do Pedido e do BotaoContatoPedido: aba nova, param certo. */
  const abrirNoInbox = (conversationId: string) => {
    window.open(`/whatsapp-inbox?conversation=${conversationId}`, '_blank');
  };

  const canonical = useMemo(() => canonicalize(telefone), [telefone]);

  const { data: conversation, isLoading, error: erroConversa, refetch } = useQuery({
    queryKey: ['mini-conversa-full', conversationId || canonical],
    enabled: open && !!(conversationId || canonical),
    queryFn: async (): Promise<Conversation | null> => {
      // Busca a conversa do cliente em QUALQUER conexão, a mais ativa. Antes
      // restringia à conexão de 'atendimento' e perdia a conversa que está na
      // conexão oficial (ex.: onde o robô de boas-vindas enviou). É a mesma
      // conversa do Inbox — só trazida aqui de forma compacta.
      let consulta = supabase
        .from('whatsapp_conversations')
        .select(`
          id, telefone, wa_id, campanha_id, destinatario_id, lead_id, cliente_cnpj,
          agente_ia_atendendo, meta_window_expires_at, canal_atual,
          owner_user_id, status, tags, unread_count,
          last_message_preview, last_message_at, last_message_status,
          last_message_direction, last_inbound_at, last_outbound_at,
          first_inbound_at, finalizada_em, created_at, contact_name,
          contact_avatar_url,
          connection_id,
          owner:owner_user_id ( nome, foto_url ),
          connection:connection_id ( nome, telefone, provider )
        `);
      consulta = conversationId
        ? consulta.eq('id', conversationId)
        : consulta.eq('telefone_canonico', canonical).order('last_message_at', { ascending: false, nullsFirst: false });
      const { data, error } = await consulta.limit(1).maybeSingle();
      if (error) throw error;

      if (!data) return null;

      return {
        id: data.id,
        telefone: data.telefone,
        wa_id: data.wa_id,
        campanha_id: data.campanha_id,
        destinatario_id: data.destinatario_id,
        lead_id: data.lead_id,
        cliente_cnpj: data.cliente_cnpj,
        agente_ia_atendendo: !!data.agente_ia_atendendo,
        meta_window_expires_at: data.meta_window_expires_at,
        canal_atual: data.canal_atual,
        owner_user_id: data.owner_user_id,
        status: data.status as Conversation['status'],
        tags: data.tags || [],
        unread_count: data.unread_count || 0,
        last_message_preview: data.last_message_preview,
        last_message_at: data.last_message_at,
        last_message_status: data.last_message_status,
        last_message_direction: data.last_message_direction,
        last_inbound_at: data.last_inbound_at,
        last_outbound_at: data.last_outbound_at,
        first_inbound_at: data.first_inbound_at,
        finalizada_em: data.finalizada_em,
        created_at: data.created_at,
        contact_name: data.contact_name,
        // Sem este campo o ChatWindow recebia fotoUrl=undefined e o cliente
        // aparecia SEMPRE com o boneco genérico — mesmo tendo foto no Inbox.
        contact_avatar_url: data.contact_avatar_url,
        connection_id: data.connection_id,
        owner_nome: (data as any).owner?.nome || null,
        owner_foto: (data as any).owner?.foto_url || null,
        connection_provider: (data as { connection?: { provider?: string } }).connection?.provider || null,
        connection_nome: (data as any).connection?.nome || null,
        connection_telefone: (data as any).connection?.telefone || null,
      } satisfies Conversation;
    },
  });

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-[660px] sm:max-w-[660px] max-w-full flex flex-col p-0 gap-0 bg-card">
        {isLoading && (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && !conversation && (
          <div className="flex flex-col items-center justify-center flex-1 text-muted-foreground text-sm gap-2">
            <MessageSquare className="h-8 w-8 opacity-40" />
            <p>{erroConversa ? 'Não foi possível carregar a conversa. Tente novamente.' : 'Conversa não encontrada ou sem acesso à conexão.'}</p>
            <p className="text-xs">Telefone: {telefone}</p>
          </div>
        )}

        {!isLoading && conversation && (
          <div className="flex flex-col h-full">
            {/* O ✕ do Sheet é `absolute right-4 top-4` — com a barra em
                 `justify-end` ele caía EM CIMA do botão ("Abrir no Inb✕").
                 O botão vem para a esquerda, e o `pr-12` reserva o canto do ✕
                 de qualquer jeito. */}
              <div className="flex h-10 flex-shrink-0 items-center gap-2 border-b border-border-warm bg-surface pl-3 pr-12">
                {contexto && (
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-bold uppercase tracking-[1.2px] text-primary">{contexto.titulo}</div>
                    <div className="truncate text-[11px] text-foreground/90">{contexto.detalhe}</div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => abrirNoInbox(conversation.id)}
                  className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  <ExternalLink className="h-3 w-3" />
                  Abrir no Inbox
                </button>
              </div>
            <div className="flex-1 min-h-0">
              <ChatWindow
                key={conversation.id}
                conversation={conversation}
                onConversationChanged={() => { void refetch(); }}
              />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
